import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import Home from './pages/Home';
import MalwareAnalysis from './pages/products/MalwareAnalysis';
import ReverseModel from './pages/products/ReverseModel';
import VulnMining from './pages/products/VulnMining';
import FineTuning from './pages/products/FineTuning';
import GpuCloud from './pages/products/GpuCloud';
import LlmPlatform from './pages/products/LlmPlatform';
import Plans from './pages/Plans';
import Terms from './pages/Terms';

function AppContent() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/products/malware-analysis" element={<MalwareAnalysis />} />
      <Route path="/products/reverse-model" element={<ReverseModel />} />
      <Route path="/products/vulnerability-mining" element={<VulnMining />} />
      <Route path="/products/fine-tuning" element={<FineTuning />} />
      <Route path="/products/gpu-cloud" element={<GpuCloud />} />
      <Route path="/products/llm-platform" element={<LlmPlatform />} />
      <Route path="/plans/*" element={<Plans />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppWithTheme() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const langSyncedRef = useRef(false);

  useEffect(() => {
    const lang = user?.preferences?.language;
    if (lang && !langSyncedRef.current) {
      langSyncedRef.current = true;
      localStorage.setItem('etherworks-language', lang);
      if (lang !== i18n.language) {
        i18n.changeLanguage(lang);
      }
    }
    if (!user) {
      langSyncedRef.current = false;
    }
  }, [user?.preferences?.language, i18n]);

  return (
    <ThemeProvider userTheme="system">
      <BrowserRouter>
        <AppContent />
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              background: 'hsl(var(--card) / 0.85)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              color: 'hsl(var(--foreground))',
              border: '1px solid hsl(var(--border) / 0.5)',
              borderRadius: '12px',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.1)',
              fontSize: '13px',
              fontWeight: 500,
              padding: '10px 14px',
              letterSpacing: '-0.01em',
            },
            success: { iconTheme: { primary: '#34c759', secondary: 'white' } },
            error: { iconTheme: { primary: '#ff3b30', secondary: 'white' } },
          }}
        />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppWithTheme />
    </AuthProvider>
  );
}
